import { Controller } from "@hotwired/stimulus"

/**
 * Auto-subscribes to the MusicVideoChannel when the wizard page loads.
 * When a generation/extension completes, reloads the page so the UI
 * reflects the new state (candidates, Veo clips, etc.) without the
 * user having to manually refresh.
 *
 * Usage (on the wizard container):
 *   <div data-controller="music-video-updates"
 *        data-music-video-updates-config-id-value="146">
 *   </div>
 */
export default class MusicVideoUpdatesController extends Controller {
  static values = {
    configId: Number,
  }

  connect() {
    if (!this.configIdValue || !window.MusicVideoChannel) return

    this._subscription = window.MusicVideoChannel.subscribe(this.configIdValue, {
      onCompleted: (data) => this._handleCompleted(data),
      onFailed: (data) => this._handleFailed(data),
      onStarted: (data) => this._handleStarted(data),
    })
  }

  disconnect() {
    if (this._subscription) {
      this._subscription.unsubscribe()
      this._subscription = null
    }
  }

  _handleCompleted(data) {
    console.log("[MusicVideoUpdates] Generation completed:", data.type, data.status)

    // Show a brief toast before reloading so the user understands what happened.
    this._showToast(this._completedMessage(data), "success")

    // Small delay so the toast is visible before the page refreshes.
    setTimeout(() => {
      window.location.reload()
    }, 800)
  }

  _handleFailed(data) {
    console.log("[MusicVideoUpdates] Generation failed:", data.type, data.error)
    this._showToast(data.error || "Generation failed — check the logs.", "error")

    // Reload after a longer delay so the user can read the error.
    setTimeout(() => {
      window.location.reload()
    }, 3000)
  }

  _handleStarted(data) {
    console.log("[MusicVideoUpdates] Generation started:", data.type)
    // No reload — the page already shows spinners from the flag in config_data.
  }

  _completedMessage(data) {
    switch (data.type) {
      case "scene_generation_update":
        return `Scene candidates generated (${data.candidates_count || "?"} images)`
      case "veo_loop_update":
        return "Veo loop generated — refresh to preview"
      case "veo_extend_update":
        return `Veo clip extended to ${data.clip_duration_sec || "?"}s`
      case "effect_veo_update":
        return "Veo effect clip generated"
      default:
        return "Update complete"
    }
  }

  _showToast(message, variant = "info") {
    // Create a simple toast notification at the top of the page.
    const toast = document.createElement("div")
    toast.className = `fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium max-w-md transition-opacity duration-300 ${
      variant === "success"
        ? "bg-green-600 text-white"
        : variant === "error"
          ? "bg-red-600 text-white"
          : "bg-indigo-600 text-white"
    }`
    toast.textContent = message
    document.body.appendChild(toast)

    // Auto-remove after 5s (or sooner if page reloads).
    setTimeout(() => toast.remove(), 5000)
  }
}
